/* renderSheets(): wraps page bodies into physical sheets — size/padding/font, colour mode,
 * language direction, watermark, copies (ORIGINAL / COPY n of N) and the print footer. */
import type { CSSProperties, ReactNode } from "react";
import { docDef } from "../docs";
import { FONT_PT } from "../paginate";
import type { SheetPage } from "./data";
import type { PCtx } from "./kit";

export interface RenderedSheet { key: string; label: string; node: ReactNode }

export function renderSheets(c: PCtx, pages: SheetPage[], forPrint: boolean): RenderedSheet[] {
  const s = c.s, d = c.d, fam = d.fam;
  const copies = Math.max(1, Math.min(10, +s.copies || 1));
  const tot = pages.length;
  const ref = docDef(s.doc).ref + "-" + (s.period || c.today.slice(0, 7));
  const out: RenderedSheet[] = [];
  const wmText: Record<string, string> = { copy: c.lb1("COPY", "نسخة"), conf: c.lb1("CONFIDENTIAL", "سري"), draft: c.lb1("DRAFT", "مسودة"), orig: c.lb1("ORIGINAL", "أصل") };
  for (let cp = 0; cp < copies; cp++) {
    pages.forEach((pg, i) => {
      const copyLbl = copies > 1 ? (cp === 0 ? c.lb1("ORIGINAL", "الأصل") : c.lb1("COPY " + (cp + 1) + " of " + copies, "نسخة " + (cp + 1) + " من " + copies)) : "";
      const foot = pg.bare || fam !== "page" ? null : (
        <div className="sfoot">
          <span>{c.lb1("Printed ", "طُبع ")}{c.gd(c.today)} {c.nowHm} · {c.printedBy}</span>
          <span className="num">{ref}{copyLbl ? " · " + copyLbl : ""}{s.duplex ? " · " + c.lb1("2-sided", "وجهين") : ""}</span>
          <span>{s.pageNo ? c.lb1("Page ", "صفحة ") + (i + 1) + c.lb1(" of ", " من ") + tot : ""}</span>
        </div>
      );
      const wm = s.wm !== "none" ? <div className="wm">{wmText[s.wm]}</div> : copyLbl && cp > 0 && fam === "page" ? <div className="wm">{c.lb1("COPY", "نسخة")}</div> : null;
      const style: CSSProperties = { width: d.w + "mm", height: d.h ? d.h + "mm" : undefined, padding: d.m + "mm", fontSize: FONT_PT[s.size] + "pt" };
      const cls = "sheet fam-" + fam + " c-" + s.color + (s.lang === "ar" ? " rtl" : "") + (s.lang === "bi" ? " bi" : "");
      const label = c.lb1("Page ", "صفحة ") + (cp * tot + i + 1) + (copyLbl ? " · " + copyLbl : "");
      const sheet = (
        <div className={cls} dir={s.lang === "ar" ? "rtl" : "ltr"} style={style} data-testid={forPrint ? undefined : "sheet"} data-sheet-w={d.w} data-sheet-h={d.h}>
          {wm}<div className="sbody">{pg.body}</div>{foot}
        </div>
      );
      out.push({
        key: cp + "-" + pg.key + "-" + i, label,
        node: forPrint ? sheet : <div className="sheet-wrap"><div className="sheet-label" data-testid="sheet-label">{label}</div>{sheet}</div>,
      });
    });
  }
  return out;
}
